/**
 * A singleton class for managing Axios request cancellation using `AbortController`.
 * Ensures that only one active request at a time can be executed.
 * If a new request is started, the previous one is automatically aborted.
 */
import { isString } from 'lodash'

import { isStringEmpty } from '@/utils/general'

const isValidArgument = arg => isString(arg) && !isStringEmpty(arg)

class AppAbortController {
  constructor() {
    /**
     * Holds the collection of AbortController instances, grouped by API handler layer name.
     * @type {Map<string, Map<string, AbortController>>}
     */
    this.controllers = new Map()
  }

  /**
   * Returns an `AbortSignal` for the current request.
   * If a previous request exists, it is aborted before creating a new signal.
   *
   * @returns {AbortSignal} The abort signal to be used in Axios requests.
   *
   * @note
   * We should use AppAbortController instance with 2 required arguments:
   * `apiHandlerName` and `requestMethodName`.
   * to prevent canceling other requests from the same layer.
   * and prevent canceling same request name from different layers.
   * `requestMethodName` is the name of the method in the API handler class.
   *
   * @example
   * import AppAbortController from '@/api/abort-controller'
   *
   * const abortController = new AppAbortController()
   *
   * class SomeApiHandler extends ApiHandler {
   *   async someRequest() {
   *     const { data } = await this.http.get('/some-request', {
   *        signal: abortController.getSignal(this.constructor.name, 'someRequest')
   *     })
   *     return data
   *   }
   *
   *   async otherRequest() {
   *     const { data } = await this.http.get('/some-request', {
   *        signal: abortController.getSignal(this.constructor.name, 'otherRequest')
   *     })
   *     return data
   *   }
   * }
   *
   * export default SomeApiHandler
   */

  getSignal(apiHandlerName = '', requestMethodName = '') {
    if (!isValidArgument(requestMethodName) || !isValidArgument(apiHandlerName)) {
      throw new Error('apiHandlerName and requestMethodName must be non-empty strings')
    }

    if (!this.controllers.has(apiHandlerName)) {
      this.controllers.set(apiHandlerName, new Map())
    }

    const apiHandlerControllers = this.controllers.get(apiHandlerName)

    if (apiHandlerControllers.has(requestMethodName)) {
      apiHandlerControllers.get(requestMethodName).abort()
    }

    apiHandlerControllers.set(requestMethodName, new AbortController())
    return apiHandlerControllers.get(requestMethodName).signal
  }
}

export default AppAbortController
